import React, { useEffect, useState } from "react";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { DestinationDTO } from "../../shared/dtos/destinationsDTO";
import { OriginDTO } from "../../shared/dtos/originDTO";
import { setDestination, setOrigin } from "../../features/map/mapSlice";
import { useAppDispatch } from "../../app/hooks";
import { DirectionType } from "../../shared/constants/DirectionType";
import { AutocompletionRequest } from "react-google-places-autocomplete/build/GooglePlacesAutocomplete.types";

interface IPlaceAutocomplete {
  type: string;
  placeholder?: any;
  callback?: (data: any) => void;
  onChange?: (value: any) => void;
  countryCode?: string;
  worldwide?: boolean;
  initialAddress?: string;
}

const geoDataToDestination = (
  value: any | null,
  response: any,
): DestinationDTO => {
  return {
    id: value?.value?.place_id,
    destinationLatitude: response[0].geometry?.location?.lat(),
    destinationLongitude: response[0]?.geometry?.location?.lng(),
    destinationAddress: value?.label,
  };
};

const geoDataToOrigin = (value: any | null, response: any): OriginDTO => {
  return {
    id: value?.value?.place_id,
    originLatitude: response[0].geometry?.location?.lat(),
    originLongitude: response[0]?.geometry?.location?.lng(),
    originAddress: value?.label,
  };
};

const MONTEVIDEO_SOUTH_WEST = { lat: -34.93, lng: -56.33 };
const MONTEVIDEO_NORTHEAST = { lat: -34.78, lng: -55.95 };

const ComponentPlacesAutocomplete: React.FC<IPlaceAutocomplete> = ({
  type,
  placeholder,
  callback,
  onChange,
  countryCode,
  worldwide = false,
  initialAddress,
}) => {
  const [value, setValue] = useState<any>(() => {
    if (!initialAddress?.trim()) {
      return null;
    }

    return {
      label: initialAddress.trim(),
      value: { place_id: "persisted-origin" },
    };
  });
  const dispatch = useAppDispatch();

  const currentValue =
    (type === DirectionType.MultiDestination ||
      type === DirectionType.Charter) &&
    placeholder
      ? placeholder
      : value;

  const normalizedValue = React.useMemo(() => {
    if (!currentValue) return null;
    if (typeof currentValue === "string")
      return { label: currentValue, value: { place_id: currentValue } };
    if (currentValue.value && currentValue.value.place_id) return currentValue;
    return {
      ...currentValue,
      value: { place_id: currentValue.label || "unknown" },
    };
  }, [currentValue]);

  const autocompletionRequest = React.useMemo(() => {
    if (worldwide) {
      return {};
    }

    const normalizedCountryCode = countryCode?.trim().toLowerCase();
    if (normalizedCountryCode && normalizedCountryCode !== "int") {
      return {
        componentRestrictions: {
          country: [normalizedCountryCode],
        },
      };
    }

    return {
      bounds: [MONTEVIDEO_SOUTH_WEST, MONTEVIDEO_NORTHEAST],
    };
  }, [countryCode, worldwide]);

  useEffect(() => {
    if (
      !value?.value?.place_id ||
      value.value.place_id === "persisted-origin"
    ) {
      return;
    }

    geocodeByPlaceId(value?.value?.place_id).then((response: any) => {
      if (type === DirectionType.Origin || type === DirectionType.Charter) {
        const country = response[0].address_components.find((x: any) =>
          x.types.includes("country"),
        );
        const countryName = country.long_name;
        const direction = geoDataToOrigin(value, response);
        dispatch(setOrigin(direction, countryName));
      }
      if (type === DirectionType.Destination) {
        const direction = geoDataToDestination(value, response);
        dispatch(setDestination(direction));
      }
    });
  }, [value, dispatch, type]);

  const multipleDestinationChange = (val: any) => {
    if (callback) {
      if (val?.value?.place_id) {
        geocodeByPlaceId(val?.value?.place_id).then((response: any) => {
          const direction = geoDataToDestination(val, response);
          callback({ ...val, directionData: direction });
        });
      } else {
        callback(val);
      }
    }
  };

  return (
    <div style={{ color: "black" }}>
      <GooglePlacesAutocomplete
        apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
        apiOptions={{ language: "es" }}
        autocompletionRequest={
          autocompletionRequest as AutocompletionRequest | undefined
        }
        selectProps={{
          value: normalizedValue,
          placeholder,
          onChange: (val: any) => {
            if (type === DirectionType.MultiDestination) {
              multipleDestinationChange(val);
            } else {
              setValue(val);
            }
            if (onChange) {
              onChange(val);
            }
          },
          getOptionValue: (option: any) =>
            option.value?.place_id || option.label,
        }}
      />
    </div>
  );
};

export default ComponentPlacesAutocomplete;
